import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import BrandSelect from "./BrandSelect";
import { Brand } from "../../types/paymentProcessor";
import { useApi } from "../../hooks/useApi";
import RepresentmentStartDateField from "./RepresentmentStartDateField";
import { useRepresentmentStartDate } from "../../hooks/useRepresentmentStartDate";

interface ShopifyLegacyDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
  showRepresentmentStartDate?: boolean;
  brands: Brand[];
  refreshBrands: () => void;
  createBrandUrl?: string;
}

export default function ShopifyLegacyDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
  showRepresentmentStartDate,
  brands,
  refreshBrands,
  createBrandUrl,
}: ShopifyLegacyDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState({ shop: '', client_id: '', client_secret: '' });
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const representmentStartDate = useRepresentmentStartDate();
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setFormData({ shop: '', client_id: '', client_secret: '' });
    setSelectedBrandId('');
    representmentStartDate.reset();
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    try {
      const shop = formData.shop.trim();
      const fullShopName = shop.endsWith('.myshopify.com') ? shop : `${shop}.myshopify.com`;

      await post(apiUrl, {
        shop: fullShopName,
        clientId: formData.client_id,
        clientSecret: formData.client_secret,
        brandId: selectedBrandId,
        ...(showRepresentmentStartDate ? representmentStartDate.payload : {}),
      });

      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      setFormError(err.message || 'Failed to link Shopify Payments (custom app) account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Shopify Payments (custom app) Account</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
              https://
            </Typography>
            <TextField
              label="Shop Name"
              name="shop"
              value={formData.shop}
              onChange={handleInputChange}
              fullWidth
              required
              disabled={submitting}
              size="small"
              placeholder="your-store-name"
              helperText="Enter your Shopify shop name (without .myshopify.com)"
            />
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
              .myshopify.com
            </Typography>
          </Box>
          <TextField
            label="Client ID"
            name="client_id"
            value={formData.client_id}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
            autoComplete="off"
          />
          <TextField
            label="Client Secret"
            name="client_secret"
            type="password"
            value={formData.client_secret}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
            autoComplete="new-password"
          />
          <BrandSelect
            brands={brands}
            value={selectedBrandId}
            onChange={(brandId) => setSelectedBrandId(brandId)}
            refreshBrands={refreshBrands}
            disabled={submitting}
            createBrandUrl={createBrandUrl}
          />
          {showRepresentmentStartDate && (
            <RepresentmentStartDateField
              value={representmentStartDate.value}
              onChange={representmentStartDate.setValue}
              disabled={submitting}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !formData.shop.trim() || !formData.client_id || !formData.client_secret || !selectedBrandId || (showRepresentmentStartDate && representmentStartDate.invalid)}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
